import { action, makeObservable, observable } from "mobx";
import { API } from "aws-amplify";
import { getCampaignMessages, putCampaignMessage } from "../../graphql/queries";

// Helper function to get approved messages from localStorage
const getApprovedMessagesFromStorage = () => {
  try {
    const storedData = localStorage.getItem('annaims_approved_messages');
    if (storedData) {
      return new Set(JSON.parse(storedData));
    }
  } catch (err) {
    // Silent error handling
  }
  return new Set();
};

class ConversationListStore {
  conversations = {};
  isLoading = false;
  error = null;
  approvedMessages = getApprovedMessagesFromStorage();

  constructor() {
    makeObservable(this, {
      conversations: observable,
      isLoading: observable,
      error: observable,
      fetchConversations: action,
      updateMessage: action,
      setError: action,
      setLoading: action,
      markMessageAsApproved: action,
    });
    
    // Initialize from localStorage
    this.approvedMessages = getApprovedMessagesFromStorage();
    
    // Set up global access if not already set
    window.ANNAIMS_APPROVED_MESSAGES = window.ANNAIMS_APPROVED_MESSAGES || this.approvedMessages;
  }

  setError(error) {
    this.error = error;
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
  }
  
  // Add a function to mark a message as approved and persist to localStorage
  markMessageAsApproved(messageId) {
    this.approvedMessages.add(messageId);
    
    // Update global variable
    if (window.ANNAIMS_APPROVED_MESSAGES) {
      window.ANNAIMS_APPROVED_MESSAGES.add(messageId);
    }
    
    // Save to localStorage
    try {
      localStorage.setItem('annaims_approved_messages', 
        JSON.stringify([...this.approvedMessages]));
      
      // Dispatch event to notify about the update
      window.dispatchEvent(new CustomEvent('annaims_message_approved', { 
        detail: { messageId } 
      }));
    } catch (err) {
      // Silent error handling
    }
  }

  async fetchConversations(campaignId) {
    try {
      this.setLoading(true);
      this.setError(null);

      const response = await API.graphql({
        query: getCampaignMessages,
        variables: { campaign: campaignId },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      const conversations = JSON.parse(response.data.getCampaignMessages.body);
      
      // Create an object to store grouped messages
      let grouped_msgs = {};
      
      // For each ID in our list, create a group
      conversations.data.ids.forEach(id => {
        // Find all messages where this ID is either sender or receiver
        const messages = conversations.data.messages.filter(msg => 
          msg.sender === id || msg.receiver === id
        );
        
        if (messages.length > 0) {
          // Apply any locally approved status before returning messages
          const messagesWithLocalStatus = messages.map(msg => {
            // Check if this message is approved in localStorage
            if (this.approvedMessages.has(msg.id)) {
              return {
                ...msg,
                status: 'SENT',
                approvedAt: msg.approvedAt || new Date().toISOString()
              };
            }
            return msg;
          });
          
          grouped_msgs[id] = messagesWithLocalStatus;
        }
      });

      this.conversations = grouped_msgs;
      return grouped_msgs;
    } catch (error) {
      this.setError(error);
      return {};
    } finally {
      this.setLoading(false);
    }
  }

  async updateMessage(messageId, campaignId, message, status = 'SENT', approvedAt = null) {
    try {
      this.setLoading(true);
      this.setError(null);

      // Immediately mark as approved in localStorage to ensure UI state persists
      // even if API call fails or backend status doesn't update
      if (status === 'SENT') {
        this.markMessageAsApproved(messageId);
      }

      // Ensure we have a valid approvedAt timestamp
      const timestamp = approvedAt || new Date().toISOString();

      // Extract just the message content if it's a JSON string
      let messageContent = message;
      try {
        // Check if the message is already a JSON string
        if (typeof message === 'string' && (message.startsWith('{') || message.startsWith('['))) {
          const parsedMessage = JSON.parse(message);
          if (parsedMessage.message) {
            messageContent = parsedMessage.message;
          }
        }
      } catch (e) {
        // If parsing fails, use the original message
      }

      // The API only accepts message_id, campaign_id, and updated_message
      // We need to store status and approvedAt separately on the server
      const response = await API.graphql({
        query: putCampaignMessage,
        variables: { 
          message_id: messageId, 
          campaign_id: campaignId, 
          updated_message: messageContent // Send only the message content
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      // If the message was updated successfully, update our local cache too
      if (response.data?.putCampaignMessage) {
        // Find the conversation this message belongs to
        
        for (const [email, messages] of Object.entries(this.conversations)) {
          const messageIndex = messages.findIndex(msg => msg.id === messageId);
          if (messageIndex >= 0) {
            // Update the message in our local store
            const oldMessage = this.conversations[email][messageIndex];
            
            this.conversations[email][messageIndex] = {
              ...oldMessage,
              message: messageContent,
              status,
              approvedAt: timestamp
            };

            break;
          }
        }
      }

      return response.data?.putCampaignMessage;
    } catch (error) {
      this.setError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }
}

export default ConversationListStore; 